import {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import {
  CalendarEventAttendeeListItem,
  CalendarEventListItem,
} from 'presentations/api/schema/calendar_event';
import { CalendarSourceType } from 'presentations/api/schema/calendar_event/enum';
import { IntegrationMergedEventListItem } from 'presentations/api/schema/calendar_event/integrationMerged';
import {
  EventDetailItem,
  EventListItem,
  EventPageItem,
} from 'presentations/api/schema/event';
import { ViewerItem } from 'presentations/api/schema/user';
import { apiPath, eventEditPath } from 'presentations/routes/path';
import { useRequest } from 'presentations/views/hooks/request';
import { destroy, patch, post } from 'infras/rest_client';
import { SourceType } from 'domains/models/event_source/sourceType';
import { DisplayType } from 'domains/models/event_type/displayType';
import { VisibilityType } from 'domains/models/event/visibilityType';
import { Status } from 'domains/models/event/status';
import { autolink } from 'domains/models/event/description';
import { replaceNewLineToHtmlBr } from 'presentations/views/helpers';
import {
  canShowEventDetail,
  getEventTitle,
  getIntegrationEventUrl,
} from 'presentations/views/helpers/event';
import { eventDetailPath } from 'presentations/routes/path/event';
import { formatDateTimeRange } from 'presentations/views/helpers/formatter';
import { formatToISO } from 'infras/datetime';
import { findVideoServiceType } from 'domains/models/calendar/videoServiceType';
import { IntegrationType } from 'domains/models/account/providerType';
import { ResponseStatus } from 'domains/models/event/responseStatus';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import { PaymentMethod } from 'domains/models/event_payment/payment_method';

interface Props {
  viewer: ViewerItem;
  mergedEvent: IntegrationMergedEventListItem;
  userId: string;
  onClose: Function;
  onEditEvent?: Function;
  onCancelledEvent?: Function;
  onChangeResponseStatus?: (responseStatus: ResponseStatus) => void;
  timeZone: string;
}

export const useEventPopover = (props: Props) => {
  const router = useRouter();
  const t = translateInComponent(translations);

  const { dispatch } = useContext(DashboardPortalContext);
  const [isCancelLoading, setIsCancelLoading] = useState<boolean>(false);
  const [isCreatingPage, setIsCreatingPage] = useState<boolean>(false);
  const [responseStatus, setResponseStatus] = useState<ResponseStatus | null>(
    null,
  );
  const [detailEvent, setDetailEvent] = useState<EventDetailItem | null>(null);
  const [
    settingMenuAnchor,
    setSettingMenuAnchor,
  ] = useState<null | HTMLElement>(null);
  const [confirmCancel, setConfirmCancel] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const jicooEvent =
    props.mergedEvent.calendarSourceType === CalendarSourceType.Jicoo
      ? (props.mergedEvent.event as EventListItem)
      : null;
  const integrationEvent =
    props.mergedEvent.calendarSourceType === CalendarSourceType.Integration
      ? (props.mergedEvent.event as CalendarEventListItem)
      : null;
  const calendar = detailEvent?.calendar || props.mergedEvent.calendar || null;

  useEffect(() => {
    if (!detailEvent) return;
    if (props.mergedEvent.event.uid !== detailEvent.uid) {
      setDetailEvent(null);
    }
  }, [props.mergedEvent.event.uid]);

  const eventRequest = useRequest(
    jicooEvent?.uid
      ? apiPath(
          `/v1/teams/${props.viewer.team!.uid}/events/${
            jicooEvent.uid
          }?userId=${props.userId}`,
        )
      : null,
  );

  const isBusy =
    integrationEvent && integrationEvent.visibilityType === VisibilityType.Busy;
  const integrationRequest = useRequest(
    integrationEvent && !isBusy
      ? apiPath(
          `/v1/teams/${props.viewer.team!.uid}/events/preview_by_calendar`,
          {
            calendarId: String(integrationEvent.calendarId),
            eventId: String(integrationEvent.uid),
            userId: String(props.userId),
            startedAt: formatToISO(integrationEvent.startedAt),
            endedAt: formatToISO(integrationEvent.endedAt),
          },
        )
      : null,
  );

  useEffect(() => {
    if (!eventRequest.data) return;
    setDetailEvent(eventRequest.data);
  }, [eventRequest.data]);

  useEffect(() => {
    if (!integrationRequest.data) return;
    setDetailEvent(integrationRequest.data);
  }, [integrationRequest.data]);

  // useEffect(() => {
  //   if (containerRef.current === null) return;

  //   // 要素のリサイズイベント取得
  //   const observer = new ResizeObserver(() => {
  //     if (containerRef.current === null) return;
  //     // props.onUpdatedPopover && props.onUpdatedPopover();
  //   });
  //   observer.observe(containerRef.current);

  //   return () => {
  //     observer.disconnect();
  //   };
  // }, [containerRef.current]);

  const onClickSettingMenu = (e: SyntheticEvent<HTMLElement>) => {
    setSettingMenuAnchor(e.currentTarget);
  };

  const onDeleteEvent = () => {
    if (!jicooEvent) return;
    switch (jicooEvent.sourceType) {
      case SourceType.EventType:
        {
          if (jicooEvent.status === Status.Reserved) {
            setConfirmCancel(3);
          } else {
            setConfirmCancel(1);
          }
        }
        break;
      case SourceType.InternalCalendar:
        setConfirmCancel(2);
        break;
      default:
        break;
    }
  };

  const onEditEvent = () => {
    if (props.mergedEvent.calendarSourceType === CalendarSourceType.Jicoo) {
      if (!jicooEvent) return;

      props.onEditEvent &&
        props.onEditEvent(props.mergedEvent.event.uid, detailEvent);
    } else if (
      props.mergedEvent.calendarSourceType === CalendarSourceType.Integration
    ) {
      if (detailEvent && detailEvent.htmlLink) {
        window.open(detailEvent.htmlLink);
      }
    }
  };

  const onRescheduleEvent = () => {
    if (props.mergedEvent.calendarSourceType === CalendarSourceType.Jicoo) {
      if (!jicooEvent) return;

      if (jicooEvent.sourceType === SourceType.EventType) {
        window.open(eventEditPath(jicooEvent.uid));
      }
    }
  };

  const onSelectSettingMenu = (selectedIndex: number) => {
    setSettingMenuAnchor(null);
    if (jicooEvent && jicooEvent.isParent) {
      setConfirmCancel(1);
    } else {
      switch (selectedIndex) {
        case 0:
          onEditEvent();
          break;
        case 1:
          onDeleteEvent();
          break;
        default:
          break;
      }
    }
  };
  const onCloseSettingMenu = () => {
    setSettingMenuAnchor(null);
  };

  const onClickCreatePage = async (integrationId: IntegrationType) => {
    if (isCreatingPage) return;
    if (integrationId === IntegrationType.Notion) {
      if (!detailEvent) return;
      if (!jicooEvent) return;
      setIsCreatingPage(true);
      const data = { eventId: jicooEvent.uid };
      const result = await post(
        apiPath(`/v1/integrations/notion/pages/create`),
        { data },
      );
      const page: EventPageItem = result.data;
      const newDetailEvent = { ...detailEvent, page };
      setDetailEvent(newDetailEvent);
      setIsCreatingPage(false);
    }
  };

  const onCancelEvent = async () => {
    if (!jicooEvent) return;

    setIsCancelLoading(true);
    if (jicooEvent.sourceType === SourceType.EventType) {
      if (confirmCancel === 1) {
        const result = await patch(
          apiPath(
            `/v1/teams/${props.viewer.team!.uid}/events/${
              jicooEvent.uid
            }/cancel`,
          ),
        );
        if (!result.isSuccess) {
          setIsCancelLoading(false);
          return;
        }
      } else if (confirmCancel === 3) {
        const result = await destroy(
          apiPath(
            `/v1/teams/${props.viewer.team!.uid}/reserved_events/${
              jicooEvent.uid
            }/delete`,
          ),
        );
        if (!result.isSuccess) {
          setIsCancelLoading(false);
          return;
        }
      }
      props.onCancelledEvent && props.onCancelledEvent(jicooEvent);
    } else if (jicooEvent.sourceType === SourceType.InternalCalendar) {
      const result = await destroy(apiPath(`/v1/schedules/${jicooEvent.uid}`));
      if (!result.isSuccess) {
        setIsCancelLoading(false);
        return;
      }
      props.onCancelledEvent && props.onCancelledEvent(jicooEvent);
    } else {
      console.log(`invalid sourceType: ${jicooEvent.sourceType}`);
    }
    props.onClose();
    setConfirmCancel(null);
    setIsCancelLoading(false);
  };

  const onCloseCancelDialog = () => {
    setConfirmCancel(null);
  };

  const eventDetailLink = useMemo(() => {
    switch (props.mergedEvent.calendarSourceType) {
      case CalendarSourceType.Jicoo: {
        const event = props.mergedEvent.event as EventListItem;
        return eventDetailPath(event, router.asPath, props.userId);
      }
      case CalendarSourceType.Integration: {
        return getIntegrationEventUrl(
          props.mergedEvent,
          props.userId,
          router.asPath,
        );
      }
      default:
        return undefined;
    }
  }, [props.mergedEvent]);

  const { isHost, status, displayType, visibilityType } = useMemo(() => {
    const event = jicooEvent || integrationEvent || detailEvent;
    let isHost = null;
    if (jicooEvent) {
      if (jicooEvent.sourceType === SourceType.EventType) {
        isHost = jicooEvent.isHost;
      }
    }
    return {
      isHost,
      status: event?.status || Status.Open,
      displayType: event?.displayType || null,
      visibilityType: event?.visibilityType || null,
    };
  }, [jicooEvent, integrationEvent, detailEvent]);

  const description = useMemo(() => {
    const e = jicooEvent || integrationEvent;
    if (e?.description) {
      return autolink(replaceNewLineToHtmlBr(e.description));
    }
    return null;
  }, [jicooEvent, integrationEvent]);

  const isEnableGuestUpdate =
    (detailEvent && (detailEvent as any).isEnableGuestUpdate) || false;

  const reservationMethod = jicooEvent
    ? jicooEvent.reservationMethod
    : undefined;

  const title = useMemo(() => {
    if (props.mergedEvent.calendarSourceType === CalendarSourceType.Jicoo) {
      return getEventTitle(props.mergedEvent.event as EventListItem);
    }
    return props.mergedEvent.event.name;
  }, [props.mergedEvent.event]);

  const eventUsers = useMemo(() => {
    let users = null;
    if (!users && detailEvent) {
      users = detailEvent.users.map((user) => ({
        profileImage: user.profileImage,
        uid: user.uid,
        name: user.name,
        responseStatus: user.responseStatus,
        isOptional: user.isOptional ?? null,
        isOrganizer: user.isOrganizer,
      }));
    }
    if (!users && jicooEvent?.users) {
      users = jicooEvent?.users.map((user) => ({
        profileImage: user.profileImage,
        uid: user.uid,
        name: user.name,
        responseStatus: user.responseStatus,
        isOptional: null,
        isOrganizer: false,
      }));
    }
    if (!users && integrationEvent?.attendees) {
      users = integrationEvent?.attendees.map((attendee) => ({
        profileImage: null,
        uid: attendee.email,
        name: attendee.displayName || attendee.email,
        responseStatus: attendee.responseStatus,
        isOptional: attendee.isOptional,
        isOrganizer: attendee.isOrganizer,
      }));
    }
    return users || [];
  }, [detailEvent, jicooEvent, integrationEvent]);

  useEffect(() => {
    if (!integrationEvent) return;
    const attendee = integrationEvent.attendees.find(
      (a: CalendarEventAttendeeListItem) =>
        a.isSelf === true && a.email === props.viewer.email,
    );
    setResponseStatus(attendee?.responseStatus || null);
  }, [integrationEvent?.uid, integrationEvent?.calendarId]);

  const dateTimeString = useMemo(() => {
    const e = jicooEvent || integrationEvent!;
    if (e.startedAt && e.endedAt) {
      return formatDateTimeRange(
        formatToISO(e.startedAt, e.timeZone),
        formatToISO(e.endedAt, e.timeZone),
        props.timeZone,
        e.isAllDay,
      );
    }
    return '';
  }, [jicooEvent, integrationEvent]);

  const videoService = useMemo(() => {
    const e = jicooEvent || integrationEvent!;
    const videoServices = findVideoServiceType(e.url, e.description);
    return videoServices.length > 0 ? videoServices[0] : null;
  }, [jicooEvent, integrationEvent]);

  const isShowLocation = useMemo(() => {
    if (
      jicooEvent?.address ||
      jicooEvent?.phoneNumber ||
      jicooEvent?.customLocation
    ) {
      return true;
    }
    if (integrationEvent?.address || integrationEvent?.customLocation) {
      return true;
    }
    if (
      detailEvent?.address ||
      detailEvent?.phoneNumber ||
      detailEvent?.access
    ) {
      return true;
    }
    if (videoService) {
      return true;
    }
    return false;
  }, [detailEvent, jicooEvent, integrationEvent, videoService]);

  const calendarColor = useMemo(() => {
    const e = jicooEvent || integrationEvent!;
    let color = e.color;
    if (detailEvent?.eventType) {
      color = detailEvent.eventType.color;
    }
    return color;
  }, [jicooEvent, integrationEvent, detailEvent]);

  const isShowDetailLink = useMemo(() => {
    const isShow = canShowEventDetail(visibilityType, eventUsers, props.viewer);
    if (jicooEvent) {
      return (
        isShow && [Status.Open, Status.Request].includes(jicooEvent.status)
      );
    }
    return isShow;
  }, [jicooEvent, visibilityType, eventUsers, props.viewer]);

  const onClickAccept = async (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setResponseStatus(ResponseStatus.Accepted);
    if (props.onChangeResponseStatus) {
      props.onChangeResponseStatus(ResponseStatus.Accepted);
    }
  };

  const onClickDecline = async (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setResponseStatus(ResponseStatus.Declined);
    if (props.onChangeResponseStatus) {
      props.onChangeResponseStatus(ResponseStatus.Declined);
    }
  };

  const onClickTenative = async (e: SyntheticEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setResponseStatus(ResponseStatus.Tentative);
    if (props.onChangeResponseStatus) {
      props.onChangeResponseStatus(ResponseStatus.Tentative);
    }
  };

  const cancelDialogTitle = useMemo(() => {
    return t(`Cancel title ${confirmCancel}`);
  }, [jicooEvent, confirmCancel]);

  const cancelDialogText = useMemo(() => {
    let text = '';
    if (jicooEvent && jicooEvent.isHost) {
      if (confirmCancel) {
        text = t(`Cancel description ${confirmCancel}`);
      }
      if (confirmCancel === 1) {
        if (jicooEvent.isSeminar) {
          text += t(
            `Seminar ${jicooEvent.isParent ? 'All' : 'Guest'} Cancel Notice`,
          );
        }
        if (jicooEvent.payment?.paymentMethod === PaymentMethod.Card) {
          if (text.length > 0) {
            text += '\n\n';
          }
          text += t('Refund Notice');
        }
      }
    }
    return text;
  }, [jicooEvent, confirmCancel]);

  const providerType = integrationEvent ? integrationEvent.providerType : null;
  return {
    state: {
      isLoading:
        !isBusy && eventRequest.isLoading && integrationRequest.isLoading,
      isCancelLoading,
      confirmCancel,
      detailEvent,
      jicooEvent,
      integrationEvent,
      settingMenuAnchor,
      isShowDetailLink,
      eventDetailLink,
      isHost,
      status,
      isShowLocation,
      videoService,
      uid: props.mergedEvent.event.uid,
      title,
      displayType,
      reservationMethod,
      visibilityType,
      description,
      containerRef,
      isEnableGuestUpdate,
      isBusy,
      eventUsers,
      dateTimeString,
      calendarColor,
      isCreatingPage,
      providerType,
      responseStatus,
      calendar,
      cancelDialogTitle,
      cancelDialogText,
    },
    actions: {
      onClickSettingMenu,
      onSelectSettingMenu,
      onCloseSettingMenu,
      onCancelEvent,
      onCloseCancelDialog,
      onDeleteEvent,
      onEditEvent,
      onRescheduleEvent,
      onClickCreatePage,
      onClickAccept,
      onClickDecline,
      onClickTenative,
    },
  };
};
